import { ArrowForward, Download } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Fade,
  Slide,
  Typography,
  Zoom,
} from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useOptimizedData } from '../../hooks/useOptimizedData';
import { preloadCriticalImages } from '../../utils/performance';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
`;

const gradient = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

// Styled Components
const HeroContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  background: 'transparent',
  overflow: 'hidden',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 20%, rgba(0, 173, 181, 0.05) 0%, transparent 50%),
                 radial-gradient(circle at 70% 80%, rgba(238, 238, 238, 0.02) 0%, transparent 50%)`,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(8),
}));

const ProfileSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(8),
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    marginBottom: 0,
    marginRight: theme.spacing(6),
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 200,
  height: 200,
  border: `4px solid ${theme.palette.primary.main}`,
  boxShadow: `0 0 40px rgba(0, 173, 181, 0.3)`,
  animation: `${float} 6s ease-in-out infinite`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `0 0 60px rgba(0, 173, 181, 0.5)`,
  },
  [theme.breakpoints.down('md')]: {
    width: 150,
    height: 150,
  },
}));

const GlowRing = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -10,
  left: -10,
  right: -10,
  bottom: -10,
  borderRadius: '50%',
  background: `conic-gradient(from 0deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
  animation: `${pulse} 3s ease-in-out infinite`,
  zIndex: -1,
}));

const TextContent = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  background: 'rgba(34, 40, 49, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  border: '1px solid rgba(0, 173, 181, 0.1)',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
    flex: 1,
    padding: theme.spacing(5),
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  backgroundSize: '200% 200%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: `${gradient} 4s ease infinite`,
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.1rem, 2.5vw, 1.5rem)',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  fontWeight: 500,
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),
  maxWidth: 600,
  lineHeight: 1.6,
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(5),
  },
}));

const TagsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(5),
  },
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  background: 'rgba(0, 173, 181, 0.1)',
  color: theme.palette.primary.main,
  border: `1px solid rgba(0, 173, 181, 0.3)`,
  fontWeight: 600,
  fontSize: '0.9rem',
  padding: theme.spacing(0.5, 1),
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(0, 173, 181, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 173, 181, 0.3)',
  },
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  color: theme.palette.background.default,
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(0, 173, 181, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(0, 173, 181, 0.4)',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  borderColor: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.light,
    background: 'rgba(0, 173, 181, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const HeroSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { personalInfo, adjectives } = useOptimizedData();

  useEffect(() => {
    setIsVisible(true);
    
    // Preload critical images for better performance
    const criticalImages = [personalInfo.image];
    preloadCriticalImages(criticalImages).catch(console.warn);
  }, [personalInfo.image]);

  const handleScrollToProjects = useCallback(() => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const handleDownloadCV = useCallback(() => {
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = personalInfo.resume;
    link.download = `${personalInfo.name}_Resume.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [personalInfo.resume, personalInfo.name]);

  return (
    <HeroContainer id="hero">
      <ContentContainer maxWidth="lg">
        <ProfileSection>
          <Zoom in={isVisible} timeout={800}>
            <ProfileImageContainer>
              <GlowRing />
              <ProfileAvatar
                src={personalInfo.image}
                alt={`${personalInfo.name} - Full Stack Developer`}
              />
            </ProfileImageContainer>
          </Zoom>

          <TextContent>
            <Slide direction="up" in={isVisible} timeout={1000}>
              <Box>
                <SubTitle variant="h6">
                  👋 Hello, I'm
                </SubTitle>
                <MainTitle variant="h1">
                  {personalInfo.name}
                </MainTitle>
                <Description variant="body1">
                  Full Stack Developer crafting digital experiences with passion and precision. 
                  I specialize in building scalable web applications using modern technologies 
                  and best practices.
                </Description>
              </Box>
            </Slide>

            <Fade in={isVisible} timeout={1500}>
              <TagsContainer>
                {adjectives.map((skill: string, index: number) => (
                  <Fade in={isVisible} timeout={1500 + index * 200} key={index}>
                    <SkillChip 
                      label={skill} 
                      variant="outlined"
                    />
                  </Fade>
                ))}
              </TagsContainer>
            </Fade>

            <Slide direction="up" in={isVisible} timeout={2000}>
              <ButtonContainer>
                <PrimaryButton
                  endIcon={<ArrowForward />}
                  onClick={handleScrollToProjects}
                  size="large"
                >
                  About Me
                </PrimaryButton>
                <SecondaryButton
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownloadCV}
                  size="large"
                >
                  Download CV
                </SecondaryButton>
              </ButtonContainer>
            </Slide>
          </TextContent>
        </ProfileSection>
      </ContentContainer>
    </HeroContainer>
  );
};

export default HeroSection; 